import { useQuery } from "@tanstack/react-query"
import { type ReactNode, useState } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { layeredLayout } from "@/lib/layout"
import { cn } from "@/lib/utils"

export interface DiagramNode {
  readonly id: string
  readonly width: number
  readonly height: number
  readonly render: ReactNode
}

export interface DiagramEdge {
  readonly id: string
  readonly from: string
  readonly to: string
  readonly label?: string
  /** Hover text naming what the line carries. */
  readonly title?: string
  /** Business links are solid; cross-area bridges are drawn lighter. */
  readonly tone?: "strong" | "quiet"
}

const PAD = 24

/**
 * A small, static layered diagram: ELK places the nodes, edges are soft curves, and the whole
 * drawing scales to its column. No pan, no zoom — it is a map to read, not a canvas to operate.
 */
export function Diagram({ id, nodes, edges, className, label }: { id: string; nodes: readonly DiagramNode[]; edges: readonly DiagramEdge[]; className?: string; label: string }) {
  const layout = useQuery({
    queryKey: ["ws-diagram", id, nodes.map((n) => `${n.id}:${n.width}x${n.height}`).join(","), edges.map((e) => `${e.from}>${e.to}`).join(",")],
    queryFn: () =>
      layeredLayout(
        nodes.map((n) => ({ id: n.id, width: n.width, height: n.height })),
        edges.map((e) => ({ id: e.id, source: e.from, target: e.to })),
      ),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const [hovered, setHovered] = useState<string | null>(null)
  if (!layout.data) return <Skeleton className={cn("h-72 w-full rounded-2xl", className)} />
  const pos = layout.data
  const size = new Map(nodes.map((n) => [n.id, n]))
  let width = 0
  let height = 0
  for (const n of nodes) {
    const p = pos.get(n.id)
    if (!p) continue
    width = Math.max(width, p.x + n.width)
    height = Math.max(height, p.y + n.height)
  }
  const vw = width + PAD * 2
  const vh = height + PAD * 2
  return (
    <figure aria-label={label} dir="ltr" className={cn("w-full", className)}>
      <svg viewBox={`0 0 ${vw} ${vh}`} className="mx-auto block h-auto w-full" style={{ maxWidth: vw }}>
        <defs>
          <marker id={`${id}-arrow`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L8 4 L0 8 z" className="fill-muted-foreground/60" />
          </marker>
        </defs>
        {edges.map((e) => {
          const a = pos.get(e.from)
          const b = pos.get(e.to)
          const na = size.get(e.from)
          const nb = size.get(e.to)
          if (!a || !b || !na || !nb) return null
          const x1 = a.x + na.width + PAD
          const y1 = a.y + na.height / 2 + PAD
          const x2 = b.x + PAD
          const y2 = b.y + nb.height / 2 + PAD
          const backwards = x2 < x1
          const dx = Math.max(48, Math.abs(x2 - x1) / 2)
          const d = backwards
            ? `M ${a.x + na.width / 2 + PAD} ${a.y + na.height + PAD} C ${a.x + na.width / 2 + PAD} ${a.y + na.height + PAD + 60}, ${b.x + nb.width / 2 + PAD} ${b.y + nb.height + PAD + 60}, ${b.x + nb.width / 2 + PAD} ${b.y + nb.height + PAD}`
            : `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`
          const lit = hovered !== null && (e.from === hovered || e.to === hovered)
          const dim = hovered !== null && !lit
          return (
            <g key={e.id} className={cn("transition-opacity duration-200", dim && "opacity-20")}>
              {e.title ? <title>{e.title}</title> : null}
              <path
                d={d}
                fill="none"
                markerEnd={`url(#${id}-arrow)`}
                className={cn("transition-[stroke,stroke-width] duration-200", lit ? "stroke-foreground/80" : e.tone === "quiet" ? "stroke-border" : "stroke-muted-foreground/60")}
                strokeWidth={lit ? 2.25 : e.tone === "quiet" ? 1.25 : 1.75}
              />
              {e.label && !backwards ? (
                <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 6} textAnchor="middle" className="fill-subtle-foreground text-[11px]">
                  {e.label}
                </text>
              ) : null}
            </g>
          )
        })}
        {nodes.map((n) => {
          const p = pos.get(n.id)
          if (!p) return null
          return (
            <foreignObject key={n.id} x={p.x + PAD} y={p.y + PAD} width={n.width} height={n.height} className="overflow-visible">
              {/* Hover or focus lights the links of one node and fades the rest. */}
              <div
                className={cn("h-full transition-opacity duration-200", hovered !== null && hovered !== n.id && !edges.some((e) => (e.from === hovered && e.to === n.id) || (e.to === hovered && e.from === n.id)) && "opacity-50")}
                onMouseEnter={() => setHovered(n.id)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(n.id)}
                onBlur={() => setHovered(null)}
              >
                {n.render}
              </div>
            </foreignObject>
          )
        })}
      </svg>
    </figure>
  )
}
